'use client'

import React, { useState, useEffect } from 'react'
import { HeaderSection } from '@/components/shared/header-section'
import { useQuery } from '@apollo/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChevronRight, ChevronDown, Plus } from 'lucide-react'
import { GET_FABRIC_FEATURES_TREE } from './fabricFeaturesQueries'
import FabricFeatureGroupForm from './components/FabricFeatureGroupForm'
import FabricFeatureForm from './components/FabricFeatureForm'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { PageContent } from '@/components/shared/page-content'
import { useLanguage } from '@/context/LanguageContext'
import { Skeleton } from '@/components/ui/skeleton'
import { FabricFeatureGroup, FabricFeature, FabricFeatureGroupProfile, FabricFeatureProfile } from './types'

export default function FabricFeatures() {
  const { language: currentLanguage } = useLanguage()
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())

  const { data, loading } = useQuery(GET_FABRIC_FEATURES_TREE, {
    fetchPolicy: 'cache-and-network',
  })

  const groups: FabricFeatureGroup[] = data?.findManyFabricFeatureGroup || []
  const ungroupedFeatures: FabricFeature[] = data?.ungroupedFeatures || []

  // Expand all groups by default when data loads
  useEffect(() => {
    if (groups.length > 0 && expandedGroups.size === 0) {
      setExpandedGroups(new Set(groups.map(g => g.id)))
    }
  }, [groups])

  const openGroupForm = useOpenDrawer(FabricFeatureGroupForm)
  const openFeatureForm = useOpenDrawer(FabricFeatureForm)

  const toggleGroup = (groupId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const getGroupName = (profiles: FabricFeatureGroupProfile[]) => {
    return (
      profiles.find(
        p => p.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.name ||
      profiles[0]?.name ||
      ''
    )
  }

  const getFeatureName = (profiles: FabricFeatureProfile[]) => {
    return (
      profiles.find(
        p => p.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.name ||
      profiles[0]?.name ||
      ''
    )
  }

  const handleAddFeature = (groupId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    openFeatureForm({ id: null, defaultGroupId: groupId })
  }

  const hasContent = groups.length > 0 || ungroupedFeatures.length > 0

  return (
    <PageContent>
      <div className="flex justify-between items-center">
        <HeaderSection title="Fabric Features" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={openGroupForm}>
            New Group +
          </Button>
          <Button onClick={openFeatureForm}>New Feature +</Button>
        </div>
      </div>

      <div className="rounded-md border">
        {/* Table header */}
        <div className="grid gap-2 px-4 py-3 bg-muted/50 text-xs font-medium text-muted-foreground border-b" style={{ gridTemplateColumns: '40px 50px 1fr 150px 100px 80px' }}>
          <div></div>
          <div></div>
          <div>Name</div>
          <div>Code</div>
          <div>Type</div>
          <div className="text-right">Items</div>
        </div>

        {/* Table body */}
        {loading && !hasContent ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !hasContent ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No feature groups yet. Create a group to get started.
          </div>
        ) : (
          <div>
            {groups.map(group => {
              const isExpanded = expandedGroups.has(group.id)
              return (
                <React.Fragment key={`group-${group.id}`}>
                  {/* Group row */}
                  <div
                    className="grid gap-2 px-4 py-3 border-b hover:bg-muted/30 cursor-pointer items-center"
                    style={{ gridTemplateColumns: '40px 50px 1fr 150px 100px 80px' }}
                    onClick={() => openGroupForm({ id: group.id })}
                  >
                    <button
                      onClick={e => toggleGroup(group.id, e)}
                      className="flex items-center justify-center h-6 w-6 rounded hover:bg-muted"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    <div></div>
                    <div className="font-medium text-sm">
                      {getGroupName(group.fabric_feature_group_profiles)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {group.code || '-'}
                    </div>
                    <Badge variant="outline" className="w-fit text-xs">
                      Group
                    </Badge>
                    <div className="text-right">
                      <Badge variant="secondary" className="text-xs">
                        {group.fabric_features.length}
                      </Badge>
                    </div>
                  </div>

                  {/* Feature rows (children) */}
                  {isExpanded &&
                    group.fabric_features.map(feature => (
                      <div
                        key={`feature-${feature.id}`}
                        className="grid gap-2 px-4 py-2.5 border-b hover:bg-muted/20 cursor-pointer items-center bg-muted/5"
                        style={{ gridTemplateColumns: '40px 50px 1fr 150px 100px 80px' }}
                        onClick={() => openFeatureForm({ id: feature.id })}
                      >
                        <div></div>
                        <div className="flex items-center">
                          {feature.photo ? (
                            <img
                              src={feature.photo.src_xs ?? feature.photo.src}
                              className="w-8 h-8 object-contain rounded"
                              alt=""
                            />
                          ) : (
                            <div className="w-8 h-8 bg-muted rounded border flex items-center justify-center text-xs text-muted-foreground">
                              -
                            </div>
                          )}
                        </div>
                        <div className="text-sm pl-4">
                          {getFeatureName(feature.fabric_feature_profiles)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {feature.code || '-'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <Badge variant="secondary" className="text-xs">
                            {feature._count?.fabric_groups ?? 0}
                          </Badge>
                        </div>
                        <div></div>
                      </div>
                    ))}

                  {/* Add feature button inside expanded group */}
                  {isExpanded && (
                    <div
                      className="grid gap-2 px-4 py-2 border-b bg-muted/5"
                      style={{ gridTemplateColumns: '40px 50px 1fr' }}
                    >
                      <div></div>
                      <div></div>
                      <button
                        onClick={e => handleAddFeature(group.id, e)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground pl-4"
                      >
                        <Plus className="h-3 w-3" />
                        Add feature
                      </button>
                    </div>
                  )}
                </React.Fragment>
              )
            })}

            {/* Ungrouped section */}
            {ungroupedFeatures.length > 0 && (
              <React.Fragment>
                {/* Ungrouped header row */}
                <div
                  className="grid gap-2 px-4 py-3 border-b bg-muted/10 items-center"
                  style={{ gridTemplateColumns: '40px 50px 1fr 150px 100px 80px' }}
                >
                  <div></div>
                  <div></div>
                  <div className="font-medium text-sm text-muted-foreground italic">
                    Ungrouped
                  </div>
                  <div></div>
                  <div></div>
                  <div className="text-right">
                    <Badge variant="secondary" className="text-xs">
                      {ungroupedFeatures.length}
                    </Badge>
                  </div>
                </div>

                {ungroupedFeatures.map(feature => (
                  <div
                    key={`ungrouped-feature-${feature.id}`}
                    className="grid gap-2 px-4 py-2.5 border-b hover:bg-muted/20 cursor-pointer items-center bg-muted/5"
                    style={{ gridTemplateColumns: '40px 50px 1fr 150px 100px 80px' }}
                    onClick={() => openFeatureForm({ id: feature.id })}
                  >
                    <div></div>
                    <div className="flex items-center">
                      {feature.photo ? (
                        <img
                          src={feature.photo.src_xs ?? feature.photo.src}
                          className="w-8 h-8 object-contain rounded"
                          alt=""
                        />
                      ) : (
                        <div className="w-8 h-8 bg-muted rounded border flex items-center justify-center text-xs text-muted-foreground">
                          -
                        </div>
                      )}
                    </div>
                    <div className="text-sm pl-4">
                      {getFeatureName(feature.fabric_feature_profiles)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {feature.code || '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <Badge variant="secondary" className="text-xs">
                        {feature._count?.fabric_groups ?? 0}
                      </Badge>
                    </div>
                    <div></div>
                  </div>
                ))}
              </React.Fragment>
            )}
          </div>
        )}
      </div>
    </PageContent>
  )
}
